import { useCallback, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Paper,
  Rating,
  TextField,
  Typography,
} from '@mui/material';
import { getEmptyNewReview } from '../../interfaces/Review/NewReview';
import { addReview } from '../../api/Review';
import { useThemeColor } from '../../hooks/useThemeColor';
import { useLocalizedText } from '../../hooks/useLocalizedText';

type AddReviewCardProps = {
  productID: number;
  onClose: () => void;
};

export const AddReviewCard = ({ productID, onClose }: AddReviewCardProps) => {
  const t = useLocalizedText();

  const { primary, secondary } = useThemeColor();

  const [review, setReview] = useState(getEmptyNewReview(productID));

  const handleSubmit = useCallback(() => {
    addReview(review)
      .then(() => {
        toast.success(t('review.success_message'));
        setReview(getEmptyNewReview(productID));
        onClose();
      })
      .catch((err) => {
        toast.error(err.message);
      });
  }, [review]);

  return (
    <Paper
      elevation={3}
      sx={{
        py: 1.5,
        px: 1.5,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'start',
        gap: 1,
      }}
    >
      <Typography>{t('review.your_rating')}</Typography>

      <Rating
        value={review.rating}
        onChange={(_, newValue) =>
          setReview({ ...review, rating: newValue ?? 5 })
        }
      />

      <Typography>{t('review.your_review')}</Typography>

      <TextField
        value={review.comment}
        placeholder={t('review.placeholder')}
        onChange={(e) => setReview({ ...review, comment: e.target.value })}
        size='small'
        fullWidth
        multiline
        minRows={3}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 0.5,
          mt: 1.5,
          width: '100%',
          justifyContent: 'end',
        }}
      >
        <Button
          onClick={handleSubmit}
          variant='contained'
          sx={{
            textTransform: 'none',
            backgroundColor: primary,
            color: secondary,
          }}
        >
          {t('common.send')}
        </Button>

        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: primary,
          }}
        >
          {t('common.cancel')}
        </Button>
      </Box>
    </Paper>
  );
};
